// src/hooks/useAppConfig.js
//
// Read-only hook for the app_config table — admin-controlled global
// feature flags. Realtime-subscribed so the moment an admin flips a
// flag, every connected client updates without a page reload.
//
// Designed to be used by many components without N redundant Supabase
// subscriptions: the first component to use a given key opens the
// channel, subsequent components share the cached value via an in-module
// listener registry.
//
// Currently consumed by: BillingMaintenanceGate (and by extension, every
// billing surface — BillingSection, HomePage pricing, upgrade banners).

import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';

// Per-key cache of the latest value, error, and the set of subscribers
// waiting for them. Lives at module scope so multiple components share
// one supabase channel and one fetched value per key.
const CACHE = new Map();
const ERRORS = new Map();
const SUBSCRIBERS = new Map();
const CHANNELS = new Map();

function ensureSubscription(key, defaultValue) {
  if (CHANNELS.has(key)) return; // already subscribed

  // Initial fetch — populates the cache, then any future updates arrive
  // via the realtime channel below.
  //
  // IMPORTANT: on error we STILL flip loading=false via the subscriber
  // callbacks (with defaultValue). The most common cause of an error
  // here is the app_config table not existing yet (migration not run) —
  // in that case we want consumers to fall back to defaults and surface
  // the error so the admin UI can show "did you run the migration?"
  // instead of hanging in a forever-loading state.
  supabase
    .from('app_config')
    .select('value')
    .eq('key', key)
    .maybeSingle()
    .then(({ data, error }) => {
      if (error) {
        console.warn(`[useAppConfig] initial fetch failed for "${key}":`, error.message);
        ERRORS.set(key, error);
        CACHE.set(key, defaultValue);
        (SUBSCRIBERS.get(key) || new Set()).forEach((cb) => cb(defaultValue, error));
        return;
      }
      const value = data?.value ?? defaultValue;
      ERRORS.delete(key);
      CACHE.set(key, value);
      (SUBSCRIBERS.get(key) || new Set()).forEach((cb) => cb(value, null));
    });

  // Realtime channel — any UPDATE/INSERT to this key broadcasts the new
  // value to every subscriber. Single channel per key.
  const channel = supabase
    .channel(`app_config:${key}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'app_config',
        filter: `key=eq.${key}`,
      },
      (payload) => {
        const next = payload.new?.value ?? defaultValue;
        ERRORS.delete(key);
        CACHE.set(key, next);
        (SUBSCRIBERS.get(key) || new Set()).forEach((cb) => cb(next, null));
      }
    )
    .subscribe();

  CHANNELS.set(key, channel);
}

/**
 * Read a value from app_config with realtime updates.
 *
 * @param {string} key            Config row key (e.g. 'billing_maintenance')
 * @param {*}      defaultValue   Value to return until the initial fetch
 *                                completes, and if the row doesn't exist
 * @returns {{ value: *, loading: boolean, error: Error|null }}
 *
 * `error` is non-null when the initial fetch failed — most commonly because
 * the migration hasn't been run yet (table doesn't exist) or RLS blocks the
 * read. When non-null, `value` falls back to `defaultValue` and `loading`
 * is already false, so the UI can render a sensible placeholder + a
 * "did you run the migration?" hint instead of hanging forever.
 */
export function useAppConfig(key, defaultValue = null) {
  // If the cache already has the value (another component already
  // subscribed), use it as the initial render — skip the loading flash.
  const [value, setValue] = useState(() => CACHE.has(key) ? CACHE.get(key) : defaultValue);
  const [loading, setLoading] = useState(() => !CACHE.has(key));
  const [error, setError] = useState(() => ERRORS.get(key) || null);

  useEffect(() => {
    // Make sure we have a subscription open
    ensureSubscription(key, defaultValue);

    // Register this component as a listener. Each callback receives the
    // latest value AND any error from the initial fetch (null on success).
    if (!SUBSCRIBERS.has(key)) SUBSCRIBERS.set(key, new Set());
    const cb = (v, err) => {
      setValue(v);
      setError(err || null);
      setLoading(false);
    };
    SUBSCRIBERS.get(key).add(cb);

    // If cache already populated by an earlier consumer, reflect it now
    if (CACHE.has(key)) {
      cb(CACHE.get(key), ERRORS.get(key) || null);
    }

    return () => {
      SUBSCRIBERS.get(key)?.delete(cb);
    };
    // Note: defaultValue is intentionally excluded from the dep array.
    // We only want to re-subscribe when the key changes — the default is a
    // fallback used during the initial fetch and shouldn't tear down /
    // recreate the supabase channel just because it was passed by reference.
  }, [key]);

  return { value, loading, error };
}

/**
 * Convenience wrapper for the billing_maintenance flag. Returns a flat
 * { enabled, message, loading } shape that's nicer to consume than the
 * raw JSONB value.
 *
 * When `enabled` is true:
 *   - <BillingMaintenanceGate> renders a maintenance card instead of children
 *   - Upgrade / cancel / change-plan CTAs disable or hide
 *   - Marketing pricing buttons disable
 */
export function useBillingMaintenance() {
  const { value, loading, error } = useAppConfig('billing_maintenance', {
    enabled: false,
    message: 'Billing is temporarily under maintenance.',
  });

  return {
    enabled: value?.enabled === true,
    message: value?.message || 'Billing is temporarily under maintenance.',
    loading,
    error,
  };
}

/**
 * Async one-shot check of the billing_maintenance flag, suitable for
 * non-React-component callers (e.g. functions inside stripe.service.js
 * that need to gate checkout/upgrade/cancel flows centrally).
 *
 * Uses the cached value if a React component has already subscribed to
 * the flag in this session; otherwise does a single DB read. Either way,
 * returns the same shape as useBillingMaintenance() minus the loading
 * state.
 *
 * Fail-open: if the DB read errors (table missing, RLS issue), returns
 * `{ enabled: false }` so users aren't stuck. The admin would have seen
 * the table-missing banner in /admin-settings already.
 */
export async function checkBillingMaintenance() {
  if (CACHE.has('billing_maintenance')) {
    const value = CACHE.get('billing_maintenance');
    return {
      enabled: value?.enabled === true,
      message: value?.message || 'Billing changes are temporarily under maintenance.',
    };
  }
  try {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('key', 'billing_maintenance')
      .maybeSingle();
    if (error) {
      console.warn('[checkBillingMaintenance] read failed:', error.message);
      return { enabled: false, message: null };
    }
    // Cache the result so subsequent calls in this session are sync
    if (data?.value) CACHE.set('billing_maintenance', data.value);
    return {
      enabled: data?.value?.enabled === true,
      message: data?.value?.message || 'Billing changes are temporarily under maintenance.',
    };
  } catch (err) {
    console.warn('[checkBillingMaintenance] threw:', err);
    return { enabled: false, message: null };
  }
}
